import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import Input from 'components/Shared/Input'
import { TransparentBgButton, SecondaryButton } from 'components/Shared/Button'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { updatePartnerBranding } from 'api/partner'

const MAX_BUSINESS_NAME_LENGTH = 25

/**
 * "Business name" field — Figma node 31504:25015 (fd-WebApp,
 * "Google business name - logo settings"). Own Cancel/Save pair, saved
 * independently of the logo field.
 */
const BusinessNameField = ({ initialValue, isLoading }) => {
  const { t } = useTranslation()
  const { addNotification } = useToast()

  const [businessName, setBusinessName] = useState(initialValue)
  const [draft, setDraft] = useState(initialValue)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setBusinessName(initialValue)
    setDraft(initialValue)
  }, [initialValue])

  const handleEdit = () => {
    setDraft(businessName)
    setIsEditing(true)
  }

  const handleCancel = () => {
    setDraft(businessName)
    setIsEditing(false)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const saved = await updatePartnerBranding({ businessName: draft })
      setBusinessName(saved.businessName || '')
      setIsEditing(false)
      addNotification({ message: t('Settings.branding.saveSuccess'), variant: 'success' })
    } catch (error) {
      addNotification({ message: t('Settings.branding.saveError'), variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const isEditingMode = isEditing || !businessName

  return (
    <div className="settings-page__section" style={{ borderBottom: 'none' }}>
      <div className="settings-page__sectionBody">
        <div className="settings-page__sectionState flex-grow-1">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="settings-page__sectionTitle">{t('Settings.branding.businessName.title')}</h3>
              <p className="settings-page__sectionValue mb-2">{t('Settings.branding.businessName.description')}</p>
            </div>

            <div className="d-flex align-items-start gap-2">
              {isEditingMode && businessName ? (
                <TransparentBgButton
                  text={t('common.cancel')}
                  onClick={handleCancel}
                  color="navy"
                  fullWidth={false}
                  disabled={isSaving}
                />
              ) : null}
          
              {isEditingMode ? (
                <SecondaryButton
                  text={t('common.save')}
                  onClick={handleSave}
                  color="navy"
                  fullWidth={false}
                  loading={isSaving}
                  disabled={isLoading || !draft?.trim()}
                />
              ) : (
                <TransparentBgButton
                  text={t('common.edit')}
                  onClick={handleEdit}
                  color="navy"
                  fullWidth={false}
                  disabled={isLoading}
                />
              )}
            </div>
          </div>

          {isEditingMode ? (
            <Input
              value={draft}
              name="businessName"
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_BUSINESS_NAME_LENGTH))}
              placeholder={t('Settings.branding.businessName.placeholder')}
              inputClass="settings-page__sectionInput"
              maxLength={MAX_BUSINESS_NAME_LENGTH}
              showMax
              disabled={isSaving || isLoading}
            />
          ) : (
            <p className="settings-page__sectionValue branding-value-pill">{businessName}</p>
          )}
        </div>
      </div>
    </div>
  )
}

BusinessNameField.propTypes = {
  initialValue: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
}

export default BusinessNameField
